<script lang="ts">
  import { api } from '$shared/api';
  import { getCurrentRoute } from '$lib/router.svelte';

  let loading = $state(true);
  let error = $state('');
  let username = $state('');
  let currentFrequency = $state('');
  let message = $state('');
  let changed = $state(false);

  const route = $derived(getCurrentRoute());
  const params = $derived(new URLSearchParams(route.query));
  const token = $derived(params.get('token') ?? '');
  const freq = $derived(params.get('freq') ?? '');

  async function callApi(freqParam?: string): Promise<void> {
    loading = true;
    error = '';
    try {
      const qs = freqParam
        ? `?token=${encodeURIComponent(token)}&freq=${encodeURIComponent(freqParam)}`
        : `?token=${encodeURIComponent(token)}`;
      const data = await api.get<{ username?: string; current_frequency?: string; message?: string }>(
        `/auth/unsubscribe${qs}`,
      );
      username = data.username ?? '';
      currentFrequency = data.current_frequency ?? '';
      if (data.message) {
        message = data.message;
        changed = true;
      }
    } catch (err) {
      error = (err as Error).message || 'Something went wrong. Please try again.';
    }
    loading = false;
  }

  async function changeFrequency(newFreq: string): Promise<void> {
    await callApi(newFreq);
  }

  $effect(() => {
    if (!token) {
      error = 'No token provided.';
      loading = false;
      return;
    }
    if (freq) {
      callApi(freq);
    } else {
      callApi();
    }
  });

  function frequencyLabel(f: string): string {
    if (f === 'daily') return 'Daily';
    if (f === 'weekly') return 'Weekly';
    return 'Off';
  }
</script>

<div class="unsub-wrapper">
  <div class="unsub-card">
    <h2>Email Preferences</h2>
    {#if loading}
      <p class="status-msg">Loading...</p>
    {:else if error}
      <p class="msg--error">{error}</p>
      <a href="#/login" class="btn btn-primary full-width">Back to Login</a>
    {:else if changed}
      <p class="success-msg">{message}</p>
      {#if username}
        <p class="status-msg">Hey {username}, your preferences have been updated.</p>
      {/if}
      {#if currentFrequency === 'off'}
        <p class="alt-msg">Rather get fewer emails?</p>
        <div class="alt-actions">
          <button class="btn btn-secondary" onclick={() => changeFrequency('weekly')}>Switch to Weekly</button>
          <button class="btn btn-secondary" onclick={() => changeFrequency('daily')}>Switch to Daily</button>
        </div>
      {:else if currentFrequency === 'daily'}
        <p class="alt-msg">Rather get fewer emails?</p>
        <div class="alt-actions">
          <button class="btn btn-secondary" onclick={() => changeFrequency('weekly')}>Switch to Weekly</button>
          <button class="btn btn-secondary" onclick={() => changeFrequency('off')}>Unsubscribe</button>
        </div>
      {:else if currentFrequency === 'weekly'}
        <p class="alt-msg">Want more updates?</p>
        <div class="alt-actions">
          <button class="btn btn-secondary" onclick={() => changeFrequency('daily')}>Switch to Daily</button>
          <button class="btn btn-secondary" onclick={() => changeFrequency('off')}>Unsubscribe</button>
        </div>
      {/if}
    {:else}
      <p class="status-msg">
        Current frequency: <strong>{frequencyLabel(currentFrequency)}</strong>
      </p>
      <div class="actions">
        {#if currentFrequency !== 'daily'}
          <button class="btn btn-secondary full-width" onclick={() => changeFrequency('daily')}>Switch to Daily</button>
        {/if}
        {#if currentFrequency !== 'weekly'}
          <button class="btn btn-secondary full-width" onclick={() => changeFrequency('weekly')}
            >Switch to Weekly</button
          >
        {/if}
        {#if currentFrequency !== 'off'}
          <button class="btn btn-primary full-width" onclick={() => changeFrequency('off')}>Unsubscribe</button>
        {/if}
      </div>
    {/if}
  </div>
</div>

<style>
  .unsub-wrapper {
    display: flex;
    justify-content: center;
    padding-top: 2rem;
  }
  .unsub-card {
    max-width: 400px;
    width: 100%;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 2.5rem 2rem;
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
  }
  .unsub-card h2 {
    text-align: center;
    color: var(--text);
    font-size: 1.1rem;
    font-weight: 600;
    margin: 0 0 0.5rem;
    -webkit-text-fill-color: var(--text);
    background: none;
    background-clip: unset;
    -webkit-background-clip: unset;
  }
  .full-width {
    width: 100%;
    padding: 0.625rem;
  }
  .success-msg {
    color: var(--cyan);
    text-align: center;
    font-size: 0.9375rem;
  }
  .status-msg {
    color: var(--text-muted);
    text-align: center;
    font-size: 0.9375rem;
  }
  .alt-msg {
    color: var(--text-muted);
    text-align: center;
    font-size: 0.875rem;
    margin-top: 0.5rem;
  }
  .actions {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .alt-actions {
    display: flex;
    gap: 0.5rem;
    justify-content: center;
  }
  .alt-actions .btn {
    flex: 1;
    padding: 0.5rem;
    font-size: 0.875rem;
  }
</style>
